import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Stack, TextInput, Select, Text } from '@mantine/core';
import type { FieldSchema } from '../types/schema';
import { buildAddressLine1, type GeoapifySuggestion } from '../types/geoapify';
import AddressAutocomplete from './AddressAutocomplete';

const US_STATES = [
  'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE',
  'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE',
  'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR',
  'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA',
  'VI', 'WA', 'WV', 'WI', 'WY',
].map((s) => ({ value: s, label: s }));

interface AddressFieldProps {
  field: FieldSchema;
}

export default function AddressField({ field }: AddressFieldProps) {
  const { register, control, setValue, formState: { errors, isSubmitted } } = useFormContext<Record<string, unknown>>();
  const addressLine1Value = useWatch({ control, name: `${field.id}.addressLine1` }) as string | undefined;
  const setFieldValue = setValue as (path: string, value: unknown, options?: { shouldValidate?: boolean; shouldDirty?: boolean; shouldTouch?: boolean }) => void;

  const fieldErrors = errors[field.id] as Record<string, { message?: string }> | undefined;

  // Called by AddressAutocomplete when a place is selected from the Geoapify dropdown.
  // Maps the Geoapify GeoJSON feature properties to our five flat sub-fields.
  const handlePlaceSelect = (place: GeoapifySuggestion) => {
    const props = place.properties ?? {};

    // Build addressLine1 from housenumber + street (more reliable than address_line1 verbatim)
    const addressLine1 = buildAddressLine1(props);
    setFieldValue(`${field.id}.addressLine1`, addressLine1, { shouldValidate: true });
    setFieldValue(`${field.id}.city`, props.city ?? '', { shouldValidate: true });
    setFieldValue(`${field.id}.state`, props.state_code ?? '', { shouldValidate: true });
    setFieldValue(`${field.id}.zipcode`, props.postcode ?? '', { shouldValidate: true });
  };

  const required = field.required ? 'Required' : false;

  return (
    <Stack gap="xs" className="portal-field-block">
      {field.helpText && <Text size="xs" c="dimmed">{field.helpText}</Text>}

      {/* Address Line 1 — the Geoapify input serves as this field directly */}
      <Controller
        name={`${field.id}.addressLine1`}
        control={control}
        rules={{ required }}
        render={({ field: controllerField }) => (
          <AddressAutocomplete
            label="Address Line 1"
            required={field.required}
            error={fieldErrors?.['addressLine1']?.message ?? undefined}
            placeholder="Start typing to search, or enter manually"
            value={addressLine1Value ?? (controllerField.value as string | undefined) ?? ''}
            onPlaceSelect={handlePlaceSelect}
            onChange={(val) => controllerField.onChange(val)}
            filterByCountryCode={['us']}
          />
        )}
      />
      <TextInput
        label="Address Line 2"
        size="sm"
        radius="md"
        {...register(`${field.id}.addressLine2`)}
      />
      <TextInput
        label="City"
        required={field.required}
        error={(fieldErrors?.['city']?.message) ?? undefined}
        size="sm"
        radius="md"
        {...register(`${field.id}.city`, { required })}
      />
      <Controller
        name={`${field.id}.state`}
        control={control}
        rules={{ required }}
        render={({ field: controllerField }) => (
          <Select
            label="State"
            withAsterisk={field.required}
            data={US_STATES}
            error={isSubmitted ? (fieldErrors?.['state']?.message) ?? undefined : undefined}
            value={typeof controllerField.value === 'string' && controllerField.value !== '' ? controllerField.value : null}
            onChange={(val) => controllerField.onChange(val ?? '')}
            clearable
            size="sm"
            radius="md"
          />
        )}
      />
      <TextInput
        label="Zip Code"
        required={field.required}
        error={(fieldErrors?.['zipcode']?.message) ?? undefined}
        size="sm"
        radius="md"
        {...register(`${field.id}.zipcode`, {
          required,
          pattern: { value: /^\d{5}$/, message: 'Zip code must be 5 digits' },
        })}
      />
    </Stack>
  );
}
